import { api } from "@/api";
import { JOB_NAMES } from "./jobsService.contract";
import type {
  BlogJobRun,
  JobName,
  JobsService,
  WeeklyDigestSummary,
} from "./jobsService.contract";

/**
 * Implementação HTTP do painel do CMO (E7.4). Ver
 * `docs/blog/api-contracts.md`, seção 7, e `docs/blog/jobs.md`.
 */

const BACKOFFICE = "v1/backoffice/blog";

/** `ResponseBlogJobRun` do notifyme, em snake_case. */
interface ApiJobRun {
  id: string;
  job_name: JobName;
  status: BlogJobRun["status"];
  started_at: string;
  finished_at: string | null;
  trigger: BlogJobRun["trigger"];
  summary: string | null;
  error: string | null;
  items_processed: number;
}

const mapRun = (api: ApiJobRun): BlogJobRun => ({
  id: api.id,
  jobName: api.job_name,
  status: api.status,
  startedAt: api.started_at,
  finishedAt: api.finished_at,
  trigger: api.trigger,
  summary: api.summary,
  error: api.error,
  itemsProcessed: api.items_processed,
});

/**
 * `GET .../jobs/runs` devolve uma lista simples, mais recente primeiro. Para
 * "a última de cada job", uma chamada por job com `limit=1`.
 */
const listLatestRuns = async (): Promise<BlogJobRun[]> => {
  const results = await Promise.all(
    JOB_NAMES.map(async jobName => {
      const { data } = await api.get<ApiJobRun[]>(`${BACKOFFICE}/jobs/runs`, {
        params: { job_name: jobName, limit: 1 },
      });
      return data[0] ? mapRun(data[0]) : null;
    })
  );

  return results.filter((run): run is BlogJobRun => run !== null);
};

/** Responde 202 com a execução criada; 409 se o job já está rodando. */
const runJobNow = async (jobName: JobName): Promise<{ runId: string }> => {
  const { data } = await api.post<ApiJobRun>(`${BACKOFFICE}/jobs/${jobName}/run`);
  return { runId: data.id };
};

interface ApiWeeklyDigest {
  id: string;
  week_start_date: string;
  week_end_date: string;
  highlights_markdown: string;
  sent_at: string;
}

/**
 * Endpoint de histórico do resumo semanal ainda não está documentado em
 * `api-contracts.md` — assumindo o caminho previsível dentro do mesmo
 * namespace, a confirmar com o backend quando o `weekly-digest` estiver
 * gravando histórico.
 */
const listWeeklyDigests = async (): Promise<WeeklyDigestSummary[]> => {
  const { data } = await api.get<ApiWeeklyDigest[]>(
    `${BACKOFFICE}/weekly-digests`
  );
  return (data ?? []).map(item => ({
    id: item.id,
    weekStartDate: item.week_start_date,
    weekEndDate: item.week_end_date,
    highlightsMarkdown: item.highlights_markdown,
    sentAt: item.sent_at,
  }));
};

export const httpJobsService: JobsService = {
  listLatestRuns,
  runJobNow,
  listWeeklyDigests,
};
